import axios, { AxiosRequestHeaders } from 'axios'
import * as R from 'ramda'

import { appendUrlQueryParams, notNilNorEmpty } from '@/utils'

import { DEFAULT_HEADERS, JSONValue, RESOURCE_ENDPOINTS, ResponseData } from './constants'

const axiosInstance = axios.create({ withCredentials: true })

const makeURL = (resource: string, urlParams: JSONValue = {}, queryParams: JSONValue = {}): string => {
  const url = `${RESOURCE_ENDPOINTS[resource](urlParams as any)}`
  return notNilNorEmpty(queryParams) ? appendUrlQueryParams(url, queryParams) : url
}

const makeHeaders = (headers = {} as AxiosRequestHeaders): AxiosRequestHeaders =>
  R.mergeLeft(headers, DEFAULT_HEADERS) as AxiosRequestHeaders

/**
 * Does a POST request to the API for the given resource.
 * You can provide query params and the request body within the options object.
 *
 * @param {string} resource - one of the resources defined on the `RESOURCES` constant.
 * @param {object} [options] - object containing request options
 * @param {object} [options.urlParams] - parameters for the resource url
 * @param {object} [options.queryParams] - parameters for the resource query
 * @param {object} [options.body] - body of the request
 * @param {object} [options.headers] - headers of the request
 * @returns {Promise} - request promise
 */
const post = async (
  resource: string,
  { urlParams, body }: { urlParams: JSONValue; body: JSONValue } = { urlParams: {}, body: {} }
): Promise<ResponseData> => {
  const url = makeURL(resource, urlParams)

  const response = await axiosInstance.post(url, JSON.stringify(body), { headers: makeHeaders() })

  return response.data as ResponseData
}

/**
 * Does a GET request to the API for the given resource. You can provide url params and query params
 * within the options object
 *
 * @param {string} resource - one of the resources defined on the `RESOURCES` constant.
 * @param {object} [options] - object containing request options
 * @param {object} [options.urlParams] - parameters for the resource url
 * @param {object} [options.queryParams] - parameters for the resource query
 * @param {object} [options.headers] - headers of the request
 * @returns {Promise} - request promise
 */
const get = async (
  resource: string,
  {
    urlParams = {},
    queryParams = {},
    headers = {},
  }: { urlParams: JSONValue; queryParams: JSONValue; headers: JSONValue } = {
    urlParams: {},
    queryParams: {},
    headers: {},
  }
): Promise<ResponseData> => {
  const url = makeURL(resource, urlParams, queryParams)

  const response = await axiosInstance.get(url, { headers: makeHeaders(headers as AxiosRequestHeaders) })

  return response.data as ResponseData
}

/**
 * Does a PATCH request to the API for the given resource.
 * You can provide query params and the request body within the options object.
 *
 * @param {string} resource - one of the resources defined on the `RESOURCES` constant.
 * @param {object} [options] - object containing request options
 * @param {object} [options.urlParams] - parameters for the resource url
 * @param {object} [options.queryParams] - parameters for the resource query
 * @param {object} [options.body] - body of the request
 * @param {object} [options.headers] - headers of the request
 * @returns {Promise} - request promise
 */
const patch = async (
  resource: string,
  { urlParams, body }: { urlParams: JSONValue; body: JSONValue } = { urlParams: {}, body: {} }
): Promise<ResponseData> => {
  const url = makeURL(resource, urlParams)

  const response = await axiosInstance.patch(url, JSON.stringify(body), {
    headers: makeHeaders(),
  })

  return response.data as ResponseData
}

export const client = {
  post,
  get,
  patch,
}
